// queryviz-api.js
import { API, Auth } from 'aws-amplify';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';

const API_NAME = 'OpenQueryAPI';

async function ensureAuthed() {
  await Auth.currentAuthenticatedUser();
}

export async function getQuicksightDashboardId(queryJobId) {
  if (!queryJobId) throw new Error('queryJobId is required');

  await ensureAuthed();

  const path = `/queryviz/queryjobid/${encodeURIComponent(queryJobId)}`;
  const resp = await API.get(API_NAME, path);

  return resp?.embedUrl;
}

export async function getDashboardEmbedURL(dashboardId) {
  if (!dashboardId) throw new Error('dashboardId is required');

  await ensureAuthed();

  const session = await Auth.currentSession();
  const idToken = session.getIdToken().getJwtToken();

  const path = `/queryviz/queryjobid/${encodeURIComponent(dashboardId)}`;
  const resp = await API.get(API_NAME, path, {
    queryStringParameters: {
      openIdToken: idToken,
      dashboardIdMode: 'quicksight',
    },
  });

  if (!resp?.embedUrl || typeof resp.embedUrl !== 'string') {
    throw new Error('Query API did not return a valid embedUrl');
  }

  return resp.embedUrl;
}

export async function getDashboardExperienceEmbedURL(dashboardId, options = {}) {
  if (!dashboardId) throw new Error('dashboardId is required');

  await ensureAuthed();

  const session = await Auth.currentSession();
  const idToken = session.getIdToken().getJwtToken();

  const path = `/queryviz/queryjobid/${encodeURIComponent(dashboardId)}`;

  const queryStringParameters = {
    openIdToken: idToken,
    dashboardIdMode: options.dashboardIdMode || 'quicksight',
  };

  if (options.experience) {
    queryStringParameters.experience = options.experience;
  }

  const resp = await API.get(API_NAME, path, {
    queryStringParameters,
  });

  if (!resp?.embedUrl || typeof resp.embedUrl !== 'string') {
    throw new Error('Query API did not return a valid embedUrl');
  }

  if (options.experience === 'quick-chat') {
    if (resp.embedUrl.includes('/dashboards/')) {
      throw new Error('Query API returned a dashboard embed URL for Quick Chat.');
    }

    if (!resp.embedUrl.includes('/quick/chat')) {
      console.warn('Quick Chat embed URL did not contain /quick/chat. URL prefix:', resp.embedUrl.split('?')[0]);
    }
  }

  console.log('Dashboard experience embed response:', {
    apiName: API_NAME,
    path,
    requestedExperience: resp.requestedExperience,
    dashboardIdMode: resp.dashboardIdMode,
    resolvedDashboardId: resp.resolvedDashboardId,
    boto3Version: resp.boto3Version,
    botocoreVersion: resp.botocoreVersion,
    embedUrlPrefix: resp.embedUrl.split('?')[0],
  });

  return resp.embedUrl;
}

export async function initializeDashboardEmbed(urlStr, config = {}) {
  if (!urlStr || typeof urlStr !== 'string') {
    throw new Error('initializeDashboardEmbed requires the embed URL string');
  }

  const embeddingContext = await createEmbeddingContext();

  const frameOptions = {
    url: urlStr,
    container: config.container || '#embeddingContainer',
    height: config.height || '750px',
    width: config.width || '100%',
  };

  const contentOptions = {
    locale: 'en-US',
    toolbarOptions: {
      export: true,
      reset: true,
      undoRedo: true,
      bookmarks: true,
    },
    ...(config.contentOptions || {}),
  };

  return await embeddingContext.embedDashboard(frameOptions, contentOptions);
}

export async function initializeQuickChatEmbed(urlStr, config = {}) {
  if (!urlStr || typeof urlStr !== 'string') {
    throw new Error('initializeQuickChatEmbed requires the embed URL string');
  }

  if (urlStr.includes('/dashboards/')) {
    throw new Error('initializeQuickChatEmbed received a dashboard embed URL instead of a Quick Chat embed URL');
  }

  if (!urlStr.includes('/quick/chat')) {
    console.warn('initializeQuickChatEmbed received a URL that does not contain /quick/chat. URL prefix:', urlStr.split('?')[0]);
  }

  const embeddingContext = await createEmbeddingContext();

  if (typeof embeddingContext.embedQuickChat !== 'function') {
    throw new Error('Quick Chat requires amazon-quicksight-embedding-sdk version 2.11.0 or newer');
  }

  const frameOptions = {
    url: urlStr,
    container: config.container || '#quickChatContainer',
    height: config.height || '750px',
    width: config.width || '100%',
  };

  const agentOptions = {};
  if (config.fixedAgentId) {
    agentOptions.fixedAgentId = config.fixedAgentId;
  }

  const contentOptions = {
    ...(config.contentOptions || {}),
    ...(config.fixedAgentId ? { agentOptions } : {}),
    promptOptions: {
      allowFileAttachments: false,
      showAgentKnowledgeBoundary: true,
      showWebSearch: false,
      showChatHistory: true,
      showPromptArea: true,
      enablePrivateMode: false,
      ...(config.contentOptions?.promptOptions || {}),
    },
    footerOptions: {
      showBrandAttribution: true,
      showUsagePolicy: true,
      ...(config.contentOptions?.footerOptions || {}),
    },
  };

  return await embeddingContext.embedQuickChat(frameOptions, contentOptions);
}
